/**
 * Resolves the backend API base URL with two layers of override.
 *
 *   1. Build-time:    VITE_API_BASE_URL (set per environment in CI)
 *   2. Runtime:       ?backend=<pr-N | full URL> query param, persisted to
 *                     localStorage so subsequent navigations keep using it
 *
 * Use cases:
 * - Reviewer wants to point this UAT FE at a specific PR backend: append
 *   `?backend=pr-142` to any URL. The override sticks until cleared.
 * - Dev runs FE locally against a PR backend via the Vite proxy: leave
 *   VITE_API_BASE_URL=http://localhost:5173 and VITE_API_PROXY_TARGET set.
 *
 * Clear the override:
 *   - Append `?backend=clear` to a URL, OR
 *   - Call `clearApiBaseOverride()` from the dev console.
 */

const STORAGE_KEY = "mitigate.apiBaseOverride";
const PR_DOMAIN_TEMPLATE = "https://{slug}-api-internal.mitigate.uat.fi-sci.ai";

function normaliseOverride(raw: string): string | null {
  if (!raw || raw === "clear") return null;
  // Accept either a slug like "pr-142" or a full URL.
  if (raw.startsWith("http://") || raw.startsWith("https://")) return raw;
  return PR_DOMAIN_TEMPLATE.replace("{slug}", raw);
}

function readOverrideFromUrl(): string | null | "clear" {
  if (typeof window === "undefined") return null;
  const param = new URL(window.location.href).searchParams.get("backend");
  if (param === null) return null;
  return param === "clear" ? "clear" : param;
}

/**
 * Returns the API base URL the FE should use for requests. Pure function —
 * call it for each request (don't cache module-level) so a runtime override
 * applied via query param is picked up immediately.
 */
export function getApiBase(): string {
  if (typeof window !== "undefined") {
    const fromUrl = readOverrideFromUrl();
    if (fromUrl === "clear") {
      localStorage.removeItem(STORAGE_KEY);
    } else if (fromUrl !== null) {
      const url = normaliseOverride(fromUrl);
      if (url) localStorage.setItem(STORAGE_KEY, url);
    }
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) return stored;
  }
  return import.meta.env.VITE_API_BASE_URL || "http://localhost:3000";
}

/** Convenience for clearing the override from the dev console. */
export function clearApiBaseOverride(): void {
  if (typeof window === "undefined") return;
  localStorage.removeItem(STORAGE_KEY);
}

/** Returns the active override (or null if using the build-time default). */
export function getApiBaseOverride(): string | null {
  if (typeof window === "undefined") return null;
  return localStorage.getItem(STORAGE_KEY);
}
